import { Task } from '../types/task';
import { Project } from '../types/project';
import { User } from '../types/user';
import { DashboardMetrics, IssueAlert, TaskDistribution, ResourceMetrics, UserWorkload } from '../types/dashboard';

export const calculateDashboardMetrics = (
  tasks: Task[],
  projects: Project[],
  users: User[]
): DashboardMetrics => {
  const totalProjects = projects.length;
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const completionPercentage = totalTasks > 0 
    ? Math.round((completedTasks / totalTasks) * 100) 
    : 0;
  
  const now = new Date();
  const overdueTasks = tasks.filter(t => 
    t.due_date && 
    new Date(t.due_date) < now && 
    t.status !== 'completed'
  ).length;
  
  const unassignedTasks = tasks.filter(t => !t.assigned_to).length;
  const totalUsers = users.length;
  
  const activeUsers = users.filter(u => 
    tasks.some(t => t.assigned_to === u.id && t.status !== 'completed')
  ).length;
  
  const userWorkloads = users.map(u => ({
    userId: u.id,
    taskCount: tasks.filter(t => t.assigned_to === u.id && t.status !== 'completed').length
  }));
  
  const overloadedUsers = userWorkloads.filter(w => w.taskCount > 5).length;
  
  const emptyProjects = projects.filter(p => 
    !tasks.some(t => t.project_id === p.id)
  ).length;
  
  return {
    total_projects: totalProjects,
    total_tasks: totalTasks,
    completed_tasks: completedTasks,
    completion_percentage: completionPercentage,
    overdue_tasks: overdueTasks,
    unassigned_tasks: unassignedTasks,
    total_users: totalUsers,
    active_users: activeUsers,
    overloaded_users: overloadedUsers,
    empty_projects: emptyProjects
  };
};

export const generateIssueAlerts = (metrics: DashboardMetrics): IssueAlert[] => {
  const alerts: IssueAlert[] = [];
  
  if (metrics.overdue_tasks > 0) {
    alerts.push({
      id: 'overdue_tasks',
      label: 'Overdue Tasks',
      count: metrics.overdue_tasks,
      severity: 'critical',
      navigation_target: '/tasks',
      filter_state: { filterType: 'overdue', autoApply: true }
    });
  }
  
  if (metrics.unassigned_tasks > 0) {
    alerts.push({
      id: 'unassigned_tasks',
      label: 'Unassigned Tasks',
      count: metrics.unassigned_tasks,
      severity: 'warning',
      navigation_target: '/tasks',
      filter_state: { filterType: 'unassigned', autoApply: true }
    });
  }
  
  if (metrics.overloaded_users > 0) {
    alerts.push({
      id: 'overloaded_users',
      label: 'Overloaded Users',
      count: metrics.overloaded_users,
      severity: 'warning',
      navigation_target: '/reports',
      filter_state: { tab: 'resources' }
    });
  }
  
  if (metrics.empty_projects > 0) {
    alerts.push({
      id: 'empty_projects',
      label: 'Empty Projects',
      count: metrics.empty_projects,
      severity: 'info',
      navigation_target: '/projects',
      filter_state: { filterType: 'empty', autoApply: true }
    });
  }
  
  return alerts;
};

export const calculateTaskDistribution = (tasks: Task[]): TaskDistribution => {
  const statusBreakdown = {
    todo: tasks.filter(t => t.status === 'todo').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };
  
  const priorityBreakdown = {
    high: tasks.filter(t => t.priority === 'high').length,
    medium: tasks.filter(t => t.priority === 'medium').length,
    low: tasks.filter(t => t.priority === 'low').length
  };
  
  return {
    status_breakdown: statusBreakdown,
    priority_breakdown: priorityBreakdown
  };
};

export const calculateResourceMetrics = (users: User[], tasks: Task[]): ResourceMetrics => {
  const totalUsers = users.length;
  
  const userWorkloadList: UserWorkload[] = users.map(user => {
    const activeTasks = tasks.filter(
      t => t.assigned_to === user.id && t.status !== 'completed'
    );
    const taskCount = activeTasks.length;
    
    let workloadStatus: 'available' | 'normal' | 'overloaded' = 'available';
    if (taskCount > 5) {
      workloadStatus = 'overloaded';
    } else if (taskCount > 0) {
      workloadStatus = 'normal';
    }
    
    return {
      user_id: user.id,
      user_name: user.full_name || user.username,
      task_count: taskCount,
      workload_status: workloadStatus
    };
  });
  
  const activeUsers = userWorkloadList.filter(w => w.task_count > 0).length;
  const totalActiveTasks = userWorkloadList.reduce((sum, w) => sum + w.task_count, 0);
  const averageTasksPerUser = activeUsers > 0 
    ? parseFloat((totalActiveTasks / activeUsers).toFixed(1))
    : 0;
  
  return {
    total_users: totalUsers,
    active_users: activeUsers,
    average_tasks_per_user: averageTasksPerUser,
    user_workload_list: userWorkloadList
  };
};

export const getStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'todo': 'text-gray-600',
    'in_progress': 'text-blue-600',
    'completed': 'text-green-600'
  };
  return colorMap[status] || 'text-gray-600';
};

export const getPriorityColor = (priority: string): string => {
  const colorMap: Record<string, string> = {
    'high': 'text-red-600',
    'medium': 'text-amber-600',
    'low': 'text-green-600'
  };
  return colorMap[priority] || 'text-gray-600';
};
